"use client";
"use client";
import React, { type CSSProperties, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import cn from "classnames";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import { RichtextRenderer } from "../rich-text-render";

interface ExpandableParagraphProps {
  paragraph: string;
  moreLinkText?: string;
  lessLinkText?: string;
  lineLimitMobile: number;
  outerClassName?: string;
  buttonClassName?: string;
  lineLimitDesktop?: number;
  iconComponent?: React.ReactNode;
  defaultExpanded?: boolean;
  defaultShowButton?: boolean;
  richtextClassName?: string;
}

/**
 * Expandable rich text paragraph with line clamping for mobile/desktop.
 * Automatically detects overflow and toggles "read more / collapse" button.
 */
export default function ExpandableParagraphRichtext({
  paragraph,
  moreLinkText,
  lessLinkText,
  lineLimitMobile = 3,
  lineLimitDesktop = 6,
  outerClassName,
  defaultExpanded,
  defaultShowButton,
  buttonClassName,
  iconComponent,
  richtextClassName,
}: Readonly<ExpandableParagraphProps>) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showExpandButton, setShowExpandButton] = useState(defaultShowButton);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const lineLimit = isMobile ? lineLimitMobile : lineLimitDesktop;
  const buttonText = expanded
    ? lessLinkText || "折りたたむ"
    : moreLinkText || "続きを読む";
  const ChevronIcon = expanded ? ChevronUp : ChevronDown;

  useEffect(() => {
    if (!contentRef.current) return;

    const frame = requestAnimationFrame(() => {
      const lineHeight =
        Number.parseFloat(
          getComputedStyle(contentRef.current!).lineHeight || "1.5",
        ) || 1.5;
      const maxHeight = lineLimit * lineHeight;
      const fullHeight = contentRef.current!.scrollHeight;

      if (fullHeight > maxHeight) {
        setShowExpandButton(true);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [paragraph, lineLimit, isMobile]);

  return (
    <div
      className={cn(
        "flex flex-col text-sm leading-[1.5] md:text-base",
        outerClassName,
      )}
    >
      <div
        ref={contentRef}
        className={cn(
          "text-[var(--text-secondary)] transition-all duration-300 ease-in-out",
          !expanded && "line-clamp-custom",
        )}
        style={
          expanded
            ? undefined
            : ({
                "--line-limit": `${lineLimit}`,
              } as CSSProperties)
        }
      >
        <RichtextRenderer content={paragraph} className={richtextClassName} />
      </div>

      {showExpandButton && (
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            "flex cursor-pointer items-center justify-end gap-1 text-xs whitespace-nowrap text-[var(--text-base-primary)] md:text-base",
            buttonClassName,
          )}
        >
          <span className="flex items-center gap-1">
            {buttonText}
            {iconComponent ?? <ChevronIcon className="h-3 w-3 md:h-4 md:w-4" />}
          </span>
        </button>
      )}
    </div>
  );
}
